/**
 * Utility para gerenciar pluginSpace de forma centralizada
 * Evita chamadas diretas para /pluginspace/this que causam loops infinitos
 */

import { GET } from '../services/api'

// Cache global para evitar múltiplas chamadas
let pluginSpaceCache: any = null
let lastCacheTime = 0
let isLoading = false

// Tempo de cache em milissegundos (5 minutos)
const CACHE_DURATION = 5 * 60 * 1000

/**
 * Busca dados do pluginSpace com cache inteligente
 * Esta função deve ser usada em vez de chamadas diretas para /pluginspace/this
 */
export const getPluginSpaceData = async (forceRefresh = false): Promise<any> => {
  const now = Date.now()
  
  // Retorna cache se ainda é válido e não é refresh forçado
  if (pluginSpaceCache && !forceRefresh && (now - lastCacheTime) < CACHE_DURATION) {
    console.log('📋 Usando cache do pluginSpace (utils)')
    return pluginSpaceCache
  }

  // Tenta localStorage primeiro
  if (!forceRefresh) {
    try {
      const cachedData = localStorage.getItem('pluginSpace')
      if (cachedData) {
        const parsed = JSON.parse(cachedData)
        pluginSpaceCache = parsed
        lastCacheTime = now
        console.log('💾 Pluginspace carregado do localStorage (utils)')
        return parsed
      }
    } catch (error) {
      console.warn('Erro ao ler localStorage:', error)
    }
  }

  // Evita múltiplas chamadas simultâneas
  if (isLoading) {
    console.log('⏳ Aguardando chamada em progresso (utils)')
    // Aguarda até terminar ou timeout de 10 segundos
    let attempts = 0
    while (isLoading && attempts < 100) {
      await new Promise(resolve => setTimeout(resolve, 100))
      attempts++
    }
    return pluginSpaceCache || {}
  }

  // Faz a chamada API apenas se necessário
  console.warn('🔥 pluginSpaceUtils fazendo chamada para /pluginspace/this - EVITAR!')
  isLoading = true
  
  try {
    const res = await GET(`/pluginspace/this`)
    
    if (res && res.data && res.data[0]) {
      const data = res.data[0]
      pluginSpaceCache = data
      lastCacheTime = now
      
      // Salva no localStorage
      try {
        localStorage.setItem('pluginSpace', JSON.stringify(data))
      } catch (error) {
        console.warn('Erro ao salvar no localStorage:', error)
      }
      
      console.log('✅ PluginSpace carregado via API (utils)')
      return data
    }
    
    console.warn('⚠️ Resposta vazia da API pluginspace (utils)')
    return {}
  } catch (error) {
    console.error('❌ Erro ao carregar pluginspace (utils):', error)
    return {}
  } finally {
    isLoading = false
  }
}

/**
 * Busca GMT do pluginSpace usando cache
 */
export const getPluginSpaceGmt = async (): Promise<string> => {
  try {
    const pluginSpace = await getPluginSpaceData()
    return pluginSpace?.gmt || '+00:00'
  } catch (error) {
    console.error('Erro ao buscar GMT:', error)
    return '+00:00'
  }
}

/**
 * Limpa o cache do pluginSpace
 */
export const clearPluginSpaceCache = (): void => {
  pluginSpaceCache = null
  lastCacheTime = 0
  console.log('🗑️ Cache do pluginSpace limpo')
}

/**
 * Atualiza o cache com novos dados
 */
export const updatePluginSpaceCache = (data: any): void => {
  pluginSpaceCache = data
  lastCacheTime = Date.now()
  try {
    localStorage.setItem('pluginSpace', JSON.stringify(data))
  } catch (error) {
    console.warn('Erro ao atualizar localStorage:', error)
  }
  console.log('📝 Cache do pluginSpace atualizado')
}